import type { Region } from '@/types/database'

/**
 * The place catalogue on the client (Brian's after/places.js v2.7, Oct 30 to 31). The rows live in Supabase
 * (place_catalogue, migration 20240047); the search, the radius and the lookup are RPCs (hooks/usePlaceCatalogue.ts).
 * What stays here is the arithmetic the dial does with them: one place per compass direction first (spread), the
 * radius steps, the compass words, what GoGrov can do in a country, and which topics reach past a town.
 */

export type PlaceKind = 'city' | 'county' | 'state' | 'province' | 'region' | 'country' | 'continent' | 'world'

export interface CataloguePlace {
  id: number
  name: string
  region: string
  kind: PlaceKind
  st: string | null
  country: string | null
  country_code: string | null
  continent?: string | null
  scope?: 'national' | 'region' | 'continent' | 'world' | null
  tier: number
  lat: number | null
  lng: number | null
  news_only: boolean
  watch: boolean
  languages?: string[] | null
  region_word?: string | null
  nreg?: number | null
}

/** A place within the radius, with how far and which way (places_near). */
export interface NearPlace extends CataloguePlace {
  miles: number
  deg: number
  dir: string
  total: number
  abroad: boolean
}

/** "Next door" (Brian v1.6, "maybe we offer the search radius"): the steps a person can pick. */
export const RADII = [10, 25, 50, 100, 200] as const
export type Radius = (typeof RADII)[number]
export const isRadius = (n: unknown): n is Radius => RADII.includes(n as Radius)

export const CONTINENTS = ['North America', 'South America', 'Europe', 'Asia', 'Middle East', 'Africa', 'Oceania']

/** The countries an account looks at first, after its own (settings-places v3 NEAR0). */
export const NEAR_COUNTRIES: Record<string, string[]> = {
  'United States': ['Canada', 'Mexico', 'United Kingdom'],
  Canada: ['United States', 'Mexico', 'United Kingdom'],
  Mexico: ['United States', 'Canada'],
}

export const compass = (deg: number) => ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][Math.round(deg / 45) % 8]

export const hasXY = (p: Pick<CataloguePlace, 'lat' | 'lng'> | null | undefined): p is CataloguePlace & { lat: number; lng: number } =>
  !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng)

export const isLocal = (p: Pick<CataloguePlace, 'kind'> | null | undefined) => !!p && (p.kind === 'city' || p.kind === 'county')

/**
 * One per compass direction first (Brian v1.6: "don't want the dots clumping up"): the first place in each of eight
 * sectors, in the order given (larger first, then nearest), round by round, then the rest to make up the count.
 * Nearest first out.
 */
export function spread<T extends { miles: number; deg: number }>(ranked: T[], n: number, radius?: number): T[] {
  const byMiles = [...ranked].sort((a, b) => a.miles - b.miles)
  const limit = radius || Math.max(15, 2 * ((byMiles[3] ?? byMiles[byMiles.length - 1] ?? { miles: 0 }).miles))
  const out: T[] = []
  const taken = new Set<T>()
  for (let round = 0; out.length < n && round < 4; round++) {
    const seen = new Set<number>()
    let any = false
    for (const r of ranked) {
      if (out.length >= n) break
      if (r.miles > limit || taken.has(r)) continue
      const sec = Math.round(r.deg / 45) % 8
      if (seen.has(sec)) continue
      seen.add(sec); taken.add(r); out.push(r); any = true
    }
    if (!any) break
  }
  for (const r of ranked) { if (out.length >= n) break; if (!taken.has(r) && r.miles <= limit) { taken.add(r); out.push(r) } }
  return out.sort((a, b) => a.miles - b.miles)
}

/** "the US" in a sentence; every other country by its name. */
export const countryWord = (c: string | null | undefined) => (c === 'United States' ? 'the US' : c ?? '')

/** What GoGrov can do there today (places.js can(); not a score). */
export const coverageChip = (p: Pick<CataloguePlace, 'news_only'>): [string, string] =>
  p.news_only ? ['News only', ''] : ['Records available', 'ok']

/** How a catalogue place is held on the account (regions.scope). */
export function regionScope(p: Pick<CataloguePlace, 'kind'>): Region['scope'] {
  switch (p.kind) {
    case 'city': return 'city'
    case 'county': return 'county'
    case 'state': case 'province': case 'region': return 'state'
    default: return 'national'
  }
}

/** The line under a place's name. */
export function placeLine(p: CataloguePlace, home?: Pick<CataloguePlace, 'country'> | null): string {
  if (p.kind === 'world') return 'Worldwide'
  if (p.kind === 'country') return p.country === home?.country ? 'Nationwide' : `Nationwide · ${p.continent ?? ''}`.replace(/ · $/, '')
  const abroad = home?.country && p.country && p.country !== home.country && !p.region.endsWith(p.country)
  return abroad ? `${p.region} · ${p.country}` : p.region
}

/**
 * The topics that draw from a country, a continent or the world rather than a town (Brian's WIDE: Settings › Places'
 * wide list), with the names Settings › Places says them by ("for Politics and World news, in your topics").
 */
export const WIDE_TOPICS: Record<string, string> = {
  politics: 'Politics', geopolitics: 'World news', military: 'Military', finance: 'Finance', technology: 'Technology',
  space: 'Space', 'celebrity-news': 'Celebrity news', 'reality-tv': 'Reality TV', 'true-crime': 'True crime', cars: 'Cars',
  'history-heritage': 'History', 'artists-bands': 'Music', 'pro-sports': 'Pro sports',
}
export const isWide = (topic: string) => topic in WIDE_TOPICS

/** "for Politics and Finance, in your topics" (the National and World rows), or '' when none reach that far. */
export function wideWhy(mix: { topic: string; share: number }[]): string {
  const wide = mix.filter((m) => m.share > 0 && isWide(m.topic)).sort((a, b) => b.share - a.share).map((m) => WIDE_TOPICS[m.topic])
  return wide.length ? `for ${wide.slice(0, 2).join(' and ')}${wide.length > 2 ? ' and more' : ''}, in your topics` : ''
}

/** The place screen's words, as they'd say it there (v2.9 PLACE_WORDS). Launch is the US; the rest are ready. */
const PLACE_WORDS: Record<string, [string, string]> = {
  US: ['Any city or county in the US', 'City, county, state or country'],
  IE: ['Any town or county in Ireland', 'Town, county or country'],
  GB: ['Any town, city or county in the UK', 'Town, city, county or country'],
  AU: ['Any suburb, town or state in Australia', 'Suburb, town, state or country'],
  CA: ['Any city, town or province in Canada', 'City, province or country'],
  NZ: ['Any town or region in New Zealand', 'Town, region or country'],
}
export function placeWords(code: string | null | undefined, name?: string | null): [string, string] {
  if (code && PLACE_WORDS[code]) return PLACE_WORDS[code]
  return name ? [`Any town or region in ${name}`, 'Town, region or country'] : PLACE_WORDS.US
}

/** Language names for the two launch languages and the ones a border most often brings up. */
export const LANG_NAMES: Record<string, string> = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian', pt: 'Portuguese', nl: 'Dutch' }
export const langName = (c: string) => LANG_NAMES[c] ?? c.toUpperCase()
export const langList = (codes: string[]) => {
  const n = codes.map(langName)
  return n.length <= 1 ? n[0] ?? '' : `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`
}
